/**
 * SubGrid Component
 *
 * Reusable sub-grid wrapper with connecting line + indent visual pattern.
 * Used for expandable sub-tables across EQuIP views (PIMs facility, heatmap facility,
 * leadership clinician expansion).
 *
 * Pattern extracted from Charta AuditGrid and Onboarding Intelligence Tracker
 * connecting line implementations, refined for TailwindAdmin aesthetic.
 */

import { type ReactNode, Children } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

/* ─── Types ─── */

type SubGridDepth = 1 | 2 | 3
type SubGridVariant = 'default' | 'compact' | 'spacious'

interface SubGridProps {
  /** Whether the sub-grid is expanded */
  isExpanded: boolean
  /** Nesting depth (1-3). Deeper = lighter/thinner treatment */
  depth?: SubGridDepth
  /** Layout variant */
  variant?: SubGridVariant
  /** Whether this is the last sub-grid item (terminates vertical line early) */
  isLast?: boolean
  children: ReactNode
  className?: string
}

interface SubGridHeaderProps {
  icon?: ReactNode
  title: string
  count?: number
  className?: string
}

interface SubGridContentProps {
  children: ReactNode
  /** Enable staggered row entrance animation (0.1s per row) */
  stagger?: boolean
  className?: string
}

/* ─── Depth configuration ─── */

const DEPTH_CONFIG = {
  1: {
    padding: 'pl-4',
    background: 'bg-muted/30',
    lineWidth: 2,
    lineColor: 'bg-border',
    branchHeight: 2,
  },
  2: {
    padding: 'pl-6',
    background: 'bg-muted/20',
    lineWidth: 1.5,
    lineColor: 'bg-border/80',
    branchHeight: 1.5,
  },
  3: {
    padding: 'pl-8',
    background: 'bg-muted/10',
    lineWidth: 1,
    lineColor: 'bg-border/60',
    branchHeight: 1,
  },
} as const

/* ─── Variant configuration ─── */

const VARIANT_CONFIG = {
  default: {
    containerPadding: 'py-2',
    contentPadding: 'p-1.5',
  },
  compact: {
    containerPadding: 'py-1',
    contentPadding: 'p-1',
  },
  spacious: {
    containerPadding: 'py-3',
    contentPadding: 'p-2.5',
  },
} as const

/* ─── SubGrid (container) ─── */

export function SubGrid({
  isExpanded,
  depth = 1,
  variant = 'default',
  isLast = true,
  children,
  className,
}: SubGridProps) {
  const depthCfg = DEPTH_CONFIG[depth]
  const variantCfg = VARIANT_CONFIG[variant]

  const innerContent = (
    <div
      className={cn(
        'relative pr-3',
        depthCfg.padding,
        depthCfg.background,
        variantCfg.containerPadding,
        className,
      )}
    >
      {/* Tree connector lines container */}
      <div className="relative ml-4">
        {/* Vertical line - animated draw from parent row */}
        <motion.div
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          exit={{ scaleY: 0 }}
          transition={{ duration: 0.25, delay: 0.05 }}
          style={{
            originY: 0,
            position: 'absolute',
            left: 0,
            width: `${depthCfg.lineWidth}px`,
            top: -8,
            bottom: isLast ? 'calc(100% - 16px)' : -8,
          }}
          className={depthCfg.lineColor}
        />

        {/* Horizontal branch */}
        <div
          className={cn('absolute', depthCfg.lineColor)}
          style={{
            left: 0,
            height: `${depthCfg.branchHeight}px`,
            width: 16,
            top: 14,
          }}
        />

        {/* Content area (flush with branch end) */}
        <div className="ml-4">{children}</div>
      </div>
    </div>
  )

  return (
    <AnimatePresence initial={false}>
      {isExpanded && (
        <motion.div
          key="sub-grid-container"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{
            height: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
            opacity: { duration: 0.2, delay: 0.05 },
          }}
          className="[overflow:clip]"
        >
          {innerContent}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/* ─── SubGridHeader ─── */

export function SubGridHeader({ icon, title, count, className }: SubGridHeaderProps) {
  return (
    <div
      className={cn(
        'px-2 py-1 bg-muted border-b border-border flex items-center gap-1.5',
        className,
      )}
    >
      {icon && <span className="h-3 w-3 shrink-0 [&>svg]:h-3 [&>svg]:w-3 text-muted-foreground">{icon}</span>}
      <span className="text-xs font-medium">{title}</span>
      {count !== undefined && (
        <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-auto">
          {count}
        </Badge>
      )}
    </div>
  )
}

/* ─── SubGridContent ─── */

export function SubGridContent({ children, stagger = false, className }: SubGridContentProps) {
  const cardClass = cn(
    'rounded-lg border border-border/50 shadow-xs [overflow:clip]',
    className,
  )

  if (!stagger) {
    return (
      <Card className={cardClass}>
        {children}
      </Card>
    )
  }

  // Staggered entrance: wrap each direct child in motion.div
  const childArray = Children.toArray(children)

  return (
    <Card className={cardClass}>
      {childArray.map((child, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1, duration: 0.2 }}
        >
          {child}
        </motion.div>
      ))}
    </Card>
  )
}
